import type {
  OnboardingFamily,
  OnboardingRegistry,
  OnboardingScreen,
  OnboardingStep,
  OnboardingTask,
  OnboardingTunnelStep,
} from './onboarding-registry.types';

/**
 * ONBOARDING_REGISTRY — source de vérité unique de l'onboarding produit.
 * Encodé d'après docs/onboarding.html (Chantier 2 · Phase A · v4).
 *
 * Consommé par : le wizard (Lot 1), le moteur de score (Lot 0 backend), le
 * playbook Pulse (Lot 4) et l'enrichissement MCP (`@McpFeature`).
 *
 * Les poids (`weight`) sont à calibrer empiriquement (cf. plan § 14). Les clés
 * i18n (`*_key`) sont déclarées ici ; leurs traductions sont ajoutées dans
 * fr-FR.ts au moment où une surface les rend (Lot 1 / Lot 3).
 */

const TASKS: OnboardingTask[] = [
  // ─── Écran d'entrée ────────────────────────────────────────────────────────
  {
    id: 'entry.intention',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_ENTRY_INTENTION',
    weight: 0,
    api_endpoint: 'POST /app/onboarding/intention',
    menu_target: { route: '/account' },
    prefill: { from: 'lead_qualification', key: 'besoin' },
    mcp_feature: {
      intent:
        'Capter ce qui compte le plus pour le pro (réservation, visibilité ou les deux) afin de personnaliser la mise en scène du parcours.',
      gotchas: ['En Porte B, la valeur est dérivée de la qualification Pulse — ne pas redemander.'],
    },
  },

  // ─── Étape 1 — Identité (1.A) ──────────────────────────────────────────────
  {
    id: 'company.profile.business_name',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_BUSINESS_NAME',
    weight: 2,
    api_endpoint: 'POST /app/pro-establishment/profile',
    menu_target: { route: '/establishment', sub_tab: 'profile/edit' },
    required_scopes: ['establishment.info.update'],
    prefill: { from: 'lead_qualification', key: 'nom_pro' },
    mcp_feature: {
      intent: "Renseigner le nom commercial de l'établissement — 2 à 60 caractères.",
    },
  },
  {
    id: 'company.profile.activities',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_ACTIVITIES',
    weight: 2,
    api_endpoint: 'POST /app/professional/activities',
    // Les activités s'éditent dans la modale Profil (`profile/edit`, via
    // ActivityPicker) — il n'existe PAS de segment `profile/activities` : tout
    // segment inconnu retombe sur l'onglet Informations sans ouvrir d'éditeur.
    menu_target: { route: '/establishment', sub_tab: 'profile/edit' },
    required_scopes: ['establishment.info.update'],
    prefill: { from: 'lead_qualification', key: 'metier' },
    mcp_feature: {
      intent:
        'Choisir 1 à 5 activités dans le référentiel — pilote les défauts sectoriels, le SEO (additionalType, knowsAbout) et les libellés du site.',
    },
  },
  {
    id: 'company.profile.city',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_CITY',
    weight: 1,
    api_endpoint: 'POST /app/pro-establishment/profile',
    menu_target: { route: '/establishment', sub_tab: 'profile/edit' },
    required_scopes: ['establishment.info.update'],
    prefill: { from: 'lead_qualification', key: 'ville' },
  },
  {
    id: 'company.profile.phone',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_PHONE',
    weight: 1,
    api_endpoint: 'POST /app/pro-establishment/profile',
    menu_target: { route: '/establishment', sub_tab: 'profile/edit' },
    required_scopes: ['establishment.info.update'],
  },
  // ─── Périmètre géographique SEO (cf. plan SEO scope) ───────────────────────
  {
    id: 'company.profile.scope',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_SCOPE',
    weight: 1,
    api_endpoint: 'PATCH /app/professional/seo-scope',
    // Onglet Paramètres du site (route `/website/settings/[[...section]]`),
    // section `seo` — PAS `/website/builder` (l'éditeur, qui n'a pas d'onglet SEO).
    menu_target: { route: '/website/settings', sub_tab: 'seo' },
    required_scopes: ['establishment.info.update'],
    mcp_feature: {
      intent:
        'Choisir le périmètre géographique du SEO du pro (LOCAL = activité ancrée géographiquement, NATIONAL = chaîne / e-commerce). Default LOCAL au signup. La bascule vers NATIONAL est destructive et nécessite une confirmation explicite côté UI.',
    },
  },
  {
    id: 'company.profile.service_areas',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_SERVICE_AREAS',
    weight: 2,
    api_endpoint: 'POST /app/professional/service-areas',
    menu_target: { route: '/website/settings', sub_tab: 'seo' },
    required_scopes: ['establishment.info.update'],
    conditional: 'is_seo_local',
    mcp_feature: {
      intent:
        'Déclarer les zones ciblées par le SEO local du pro (cap 10, villes / départements / régions). Une zone est désignée principale et alimente {area} dans les templates de title/description.',
      prerequisites: [{ ref: 'company.profile.scope', why: 'Le step zones ne s’affiche qu’en mode LOCAL.' }],
    },
  },
  {
    id: 'company.media.logo',
    family: 'company',
    sub_rubrique: 'media',
    criticality: 'blocking_default',
    label_key: 'ONBOARDING_TASK_COMPANY_MEDIA_LOGO',
    weight: 1,
    api_endpoint: 'POST /app/pro-establishment/media',
    menu_target: { route: '/establishment', sub_tab: 'media/edit' },
    required_scopes: ['establishment.info.update'],
    sectoral_default_ref: 'logo_placeholder',
    mcp_feature: {
      intent:
        "Charger le logo de l'établissement. Optionnel — un placeholder typographique sectoriel est appliqué sinon.",
    },
  },

  // ─── Étape 1 — Présence en ligne (1.B) ─────────────────────────────────────
  {
    id: 'company.profile.website_url',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_default',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_WEBSITE_URL',
    weight: 0,
    api_endpoint: 'POST /app/onboarding/presence',
    menu_target: { route: '/establishment', sub_tab: 'profile/edit' },
    prefill: { from: 'lead_qualification', key: 'preview_source_url' },
    mcp_feature: {
      intent: 'Déclarer le site actuel du pro — parsé en arrière-plan pour récupérer prestations, photos et horaires.',
    },
  },
  {
    id: 'company.profile.gmb',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_default',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_GMB',
    weight: 0,
    api_endpoint: 'POST /app/pro-place/gmb/sync',
    // GMB désormais sur sa propre route (`/establishment/gmb`), pas un sous-onglet
    // de Profile. L'utilisateur arrive sur la liste GMB et clique "Ajouter".
    menu_target: { route: '/establishment/gmb' },
    required_scopes: ['establishment.google_sync.update'],
  },
  {
    id: 'company.profile.instagram',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_default',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_INSTAGRAM',
    weight: 0,
    api_endpoint: 'POST /app/onboarding/presence',
    menu_target: { route: '/establishment', sub_tab: 'profile/edit' },
  },
  {
    id: 'company.profile.other_link',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'blocking_default',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_OTHER_LINK',
    weight: 0,
    api_endpoint: 'POST /app/onboarding/presence',
    menu_target: { route: '/establishment', sub_tab: 'profile/edit' },
  },

  // ─── Étape 2 — Équipe ──────────────────────────────────────────────────────
  {
    id: 'company.team.structure',
    family: 'company',
    sub_rubrique: 'team',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_COMPANY_TEAM_STRUCTURE',
    weight: 1,
    api_endpoint: 'POST /app/team-member',
    menu_target: { route: '/establishment', sub_tab: 'team' },
    required_scopes: ['establishment.team.update'],
    mcp_feature: {
      intent: 'Savoir si le pro travaille seul ou en équipe — configure le moteur de réservation (1 ou N agendas).',
    },
  },

  // ─── Étape 3 — Prestations ─────────────────────────────────────────────────
  {
    id: 'catalog.prestations.create',
    family: 'catalog',
    sub_rubrique: 'prestations',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_CATALOG_PRESTATIONS_CREATE',
    weight: 2,
    api_endpoint: 'POST /app/prestation',
    menu_target: { route: '/catalog', sub_tab: 'prestations' },
    required_scopes: ['catalog.prestation.create'],
    // Surface MCP couverte par le manifeste `catalog.prestation.create`
    // (create-prestation.feature.ts) → pas de `mcp_feature` ici (Niveau 4 :
    // un manifeste existe, la tâche ne redéclare plus le MCP).
  },

  // ─── Étape 4 — Réservation (4.A lieux) ─────────────────────────────────────
  {
    id: 'company.places.location_types',
    family: 'company',
    sub_rubrique: 'places',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_COMPANY_PLACES_LOCATION_TYPES',
    weight: 1,
    api_endpoint: 'POST /app/pro-place',
    // sub_tab aligné sur le nom de dossier (`/establishment/place`, singulier).
    menu_target: { route: '/establishment', sub_tab: 'place' },
    required_scopes: ['establishment.place.update'],
    mcp_feature: {
      intent: 'Définir où se passent les RDV (sur place, à domicile, visio, téléphone) — au moins un lieu requis.',
    },
  },

  // ─── Étape 4 — Réservation (4.B horaires) ──────────────────────────────────
  {
    id: 'agenda.hours.opening_hours',
    family: 'agenda',
    sub_rubrique: 'hours',
    criticality: 'blocking_hard',
    label_key: 'ONBOARDING_TASK_AGENDA_HOURS_OPENING_HOURS',
    weight: 2,
    api_endpoint: 'POST /app/availability',
    // Éditeur d'horaires = `/agenda/availabilities` (deep-link existant).
    menu_target: { route: '/agenda', sub_tab: 'availabilities' },
    required_scopes: ['agenda.availability.update'],
    sectoral_default_ref: 'opening_hours',
    mcp_feature: {
      intent: "Confirmer les horaires d'ouverture — une grille hebdomadaire sectorielle est pré-remplie.",
    },
  },

  // ─── Famille 🏢 Mon entreprise — tâches différées ──────────────────────────
  {
    id: 'company.profile.complete',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_COMPLETE',
    weight: 1,
    api_endpoint: 'POST /app/pro-establishment/profile',
    menu_target: { route: '/establishment', sub_tab: 'profile/edit' },
    required_scopes: ['establishment.info.update'],
  },
  {
    id: 'company.media.photos',
    family: 'company',
    sub_rubrique: 'media',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_COMPANY_MEDIA_PHOTOS',
    weight: 1,
    api_endpoint: 'POST /app/pro-establishment/media',
    menu_target: { route: '/establishment', sub_tab: 'media/edit' },
    required_scopes: ['establishment.info.update'],
  },
  {
    id: 'company.team.member_profiles',
    family: 'company',
    sub_rubrique: 'team',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_COMPANY_TEAM_MEMBER_PROFILES',
    weight: 1,
    api_endpoint: 'POST /app/team-member',
    menu_target: { route: '/establishment', sub_tab: 'team' },
    required_scopes: ['establishment.team.update'],
    conditional: 'is_team',
  },
  {
    id: 'company.places.details',
    family: 'company',
    sub_rubrique: 'places',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_COMPANY_PLACES_DETAILS',
    weight: 1,
    api_endpoint: 'POST /app/pro-place',
    menu_target: { route: '/establishment', sub_tab: 'place' },
    required_scopes: ['establishment.place.update'],
  },
  {
    id: 'company.equipment.add',
    family: 'company',
    sub_rubrique: 'equipment',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_COMPANY_EQUIPMENT_ADD',
    weight: 0,
    api_endpoint: 'POST /app/pro-resource',
    menu_target: { route: '/establishment', sub_tab: 'equipment' },
    required_scopes: ['establishment.resource.update'],
  },
  {
    id: 'company.settings.legal',
    family: 'company',
    sub_rubrique: 'settings',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_COMPANY_SETTINGS_LEGAL',
    weight: 1,
    api_endpoint: 'POST /app/pro-establishment/profile',
    // Pas de sous-page "settings" — les champs légaux sont édités via le modal
    // Profile (legal_name, siret_number…). Fallback sur `profile`.
    menu_target: { route: '/establishment', sub_tab: 'profile/edit' },
    required_scopes: ['establishment.info.update'],
  },

  // ─── Famille 💇 Mon catalogue — tâches différées ───────────────────────────
  {
    id: 'catalog.prestations.complete',
    family: 'catalog',
    sub_rubrique: 'prestations',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_CATALOG_PRESTATIONS_COMPLETE',
    weight: 2,
    api_endpoint: 'PUT /app/prestation',
    menu_target: { route: '/catalog', sub_tab: 'prestations' },
    required_scopes: ['catalog.prestation.update'],
    conditional: 'has_prestations',
    mcp_feature: {
      intent: 'Compléter prix, durée, descriptions et photos des prestations existantes.',
    },
  },
  {
    id: 'catalog.prestations.real_photos',
    family: 'catalog',
    sub_rubrique: 'prestations',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_CATALOG_PRESTATIONS_REAL_PHOTOS',
    weight: 1,
    api_endpoint: 'PUT /app/prestation',
    menu_target: { route: '/catalog', sub_tab: 'prestations' },
    required_scopes: ['catalog.prestation.update'],
    conditional: 'has_prestations',
  },
  {
    id: 'catalog.prestations.seo_descriptions',
    family: 'catalog',
    sub_rubrique: 'prestations',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_CATALOG_PRESTATIONS_SEO_DESCRIPTIONS',
    weight: 1,
    api_endpoint: 'PUT /app/prestation',
    menu_target: { route: '/catalog', sub_tab: 'prestations' },
    required_scopes: ['catalog.prestation.update'],
    conditional: 'has_prestations',
  },
  {
    id: 'catalog.packs.create',
    family: 'catalog',
    sub_rubrique: 'packs',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_CATALOG_PACKS_CREATE',
    weight: 1,
    api_endpoint: 'POST /app/pack',
    menu_target: { route: '/catalog', sub_tab: 'packs' },
    required_scopes: ['catalog.pack.update'],
    opt_out_kind: 'packs',
    conditional: 'packs_relevant',
    sectoral_default_ref: 'packs_relevance',
  },
  {
    id: 'catalog.gift_cards.setup',
    family: 'catalog',
    sub_rubrique: 'gift_cards',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_CATALOG_GIFT_CARDS_SETUP',
    weight: 1,
    api_endpoint: 'POST /app/voucher',
    menu_target: { route: '/catalog', sub_tab: 'gift-cards' },
    required_scopes: ['sales.voucher.update'],
    opt_out_kind: 'gift_cards',
    // Stripe requis ET secteur pertinent (beauté, bien-être, resto, sport, photo).
    conditional: ['stripe_connected', 'gift_cards_relevant'],
    sectoral_default_ref: 'gift_cards_relevance',
    mcp_feature: {
      intent: 'Configurer la vente de cartes cadeaux.',
      prerequisites: [
        { ref: 'payments.stripe.create_account', why: 'Stripe doit être activé pour encaisser les cartes cadeaux.' },
      ],
    },
  },

  // ─── Famille 📅 Mon agenda — tâches différées ──────────────────────────────
  {
    id: 'agenda.hours.team_availability',
    family: 'agenda',
    sub_rubrique: 'hours',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_AGENDA_HOURS_TEAM_AVAILABILITY',
    weight: 2,
    api_endpoint: 'POST /app/availability',
    menu_target: { route: '/agenda', sub_tab: 'availabilities' },
    required_scopes: ['agenda.availability.update'],
    conditional: 'is_team',
    mcp_feature: {
      intent: "Configurer les disponibilités de chaque membre de l'équipe (horaires, synchros, exceptions).",
    },
  },
  {
    id: 'agenda.settings.review',
    family: 'agenda',
    sub_rubrique: 'settings',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_AGENDA_SETTINGS_REVIEW',
    weight: 1,
    api_endpoint: 'POST /app/availability',
    // Modale réglages agenda (onglet Affichage par défaut) = `/agenda/settings`.
    menu_target: { route: '/agenda', sub_tab: 'settings' },
    required_scopes: ['agenda.availability.update'],
    // Action de revue (pas de signal de complétion objectif) → déclinable :
    // le pro peut la marquer faite via le bouton opt-out.
    opt_out_kind: 'agenda_settings_review',
  },
  {
    id: 'agenda.google_sync.connect',
    family: 'agenda',
    sub_rubrique: 'google_sync',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_AGENDA_GOOGLE_SYNC_CONNECT',
    weight: 1,
    api_endpoint: 'POST /app/calendar-sync/google/connect',
    // Onglet Synchronisation de la modale réglages = `/agenda/settings/sync`.
    menu_target: { route: '/agenda', sub_tab: 'settings/sync' },
    required_scopes: ['agenda.sync.update'],
  },
  {
    id: 'agenda.workshops.setup',
    family: 'agenda',
    sub_rubrique: 'workshops',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_AGENDA_WORKSHOPS_SETUP',
    weight: 0,
    api_endpoint: 'POST /app/availability',
    menu_target: { route: '/agenda' },
    required_scopes: ['agenda.availability.update'],
    opt_out_kind: 'workshops',
    conditional: 'workshops_relevant',
    sectoral_default_ref: 'workshops_relevance',
  },

  // ─── Famille 💳 Mes paiements — tâches différées ───────────────────────────
  {
    id: 'payments.stripe.create_account',
    family: 'payments',
    sub_rubrique: 'stripe_account',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_PAYMENTS_STRIPE_CREATE_ACCOUNT',
    weight: 2,
    api_endpoint: 'POST /app/stripe-connect/account',
    menu_target: { route: '/establishment', sub_tab: 'payments' },
    required_scopes: ['establishment.stripe_connect.update'],
    mcp_feature: {
      intent: "Créer le compte de paiement Stripe Connect — permet au pro d'encaisser ses clients.",
    },
  },
  {
    id: 'payments.conditions.configure',
    family: 'payments',
    sub_rubrique: 'conditions',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_PAYMENTS_CONDITIONS_CONFIGURE',
    weight: 1,
    api_endpoint: 'POST /app/pro-establishment/booking-parameters',
    menu_target: { route: '/establishment', sub_tab: 'payments' },
    required_scopes: ['establishment.stripe_connect.update'],
    conditional: 'stripe_connected',
    opt_out_kind: 'online_payment',
    mcp_feature: {
      intent:
        "Configurer le mode de paiement par prestation (acompte / total / empreinte) et les conditions d'annulation.",
      prerequisites: [{ ref: 'payments.stripe.create_account', why: 'Stripe doit être activé au préalable.' }],
    },
  },

  // ─── Famille 🌐 Je personnalise mon site — tâches différées ────────────────
  {
    id: 'website.customize',
    family: 'website',
    sub_rubrique: 'customize',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_WEBSITE_CUSTOMIZE',
    weight: 1,
    api_endpoint: 'POST /app/pro-website/config',
    menu_target: { route: '/website', sub_tab: 'builder' },
    required_scopes: ['website.builder.update'],
    mcp_feature: {
      intent:
        'Personnaliser le site dans le builder (couleurs, sections, template). Validation déclarative par le pro.',
    },
  },
  {
    id: 'website.publish',
    family: 'website',
    sub_rubrique: 'publish',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_WEBSITE_PUBLISH',
    weight: 2,
    api_endpoint: 'POST /app/pro-website/publish',
    // Vue d'ensemble du site (`/website`) où vit le statut + le CTA Publier,
    // pas l'éditeur. La publication elle-même passe par la modal dédiée.
    menu_target: { route: '/website' },
    required_scopes: ['website.builder.update'],
    mcp_feature: {
      intent: 'Publier le site — il devient visible par Google. Action décisive, à confirmer explicitement.',
    },
  },
  {
    id: 'website.custom_domain',
    family: 'website',
    sub_rubrique: 'domain',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_WEBSITE_CUSTOM_DOMAIN',
    // Poids > 0 volontaire : à 0 la tâche disparaissait du score ET de la liste
    // « À configurer », mais la sous-rubrique « Nom de domaine » s'affichait
    // quand même à 0 % (poids agrégé nul) juste sous un « Mon site 100 % ».
    weight: 1,
    api_endpoint: 'POST /app/pro-website/dns/domain/select',
    menu_target: { route: '/website', sub_tab: 'domain' },
    required_scopes: ['website.builder.update'],
    mcp_feature: {
      intent: 'Connecter un nom de domaine personnalisé au site (achat ou transfert).',
      prerequisites: [{ ref: 'website.publish', why: 'Le site doit être publié pour servir le domaine.' }],
    },
  },

  // ─── Famille 👥 Mes clients — tâches différées ─────────────────────────────
  {
    id: 'customers.base.import',
    family: 'customers',
    sub_rubrique: 'base',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_CUSTOMERS_BASE_IMPORT',
    weight: 1,
    api_endpoint: 'POST /app/pro-customer',
    menu_target: { route: '/customers' },
    required_scopes: ['customer.create'],
    mcp_feature: {
      intent: 'Peupler la base clients (import application, Excel/CSV, ou saisie manuelle).',
    },
  },
  {
    id: 'customers.tags.organize',
    family: 'customers',
    sub_rubrique: 'tags',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_CUSTOMERS_TAGS_ORGANIZE',
    weight: 0,
    api_endpoint: 'POST /app/pro-tag',
    menu_target: { route: '/customers', sub_tab: 'tags' },
    required_scopes: ['establishment.tag.update'],
    conditional: 'has_customers',
  },

  // ─── Famille 📣 Ma communication — tâches différées ────────────────────────
  {
    id: 'communication.invite_clients',
    family: 'communication',
    sub_rubrique: 'invitations',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_COMMUNICATION_INVITE_CLIENTS',
    weight: 1,
    api_endpoint: 'POST /app/pro-campaign',
    // `/marketing` redirige vers les Publications (blog) ; l'invitation se fait
    // dans les Campagnes → cibler directement `/marketing/campaigns`.
    menu_target: { route: '/marketing/campaigns' },
    required_scopes: ['campaign.create'],
    mcp_feature: {
      intent: 'Inviter les clients existants à découvrir le site (email et/ou SMS, templates pré-rédigés).',
      prerequisites: [{ ref: 'customers.base.import', why: 'Une base clients est nécessaire pour pouvoir inviter.' }],
    },
  },

  // ─── Tâches du tunnel obligatoire (parcours V2, spec v4 section C) ────────
  // Poids 0 VOLONTAIRE : ces tâches servent au profil « tunnel » (évaluateurs dédiés) et ne
  // doivent pas changer le score ni la liste « À configurer » des comptes V1 existants.
  {
    id: 'communication.automatic_messages',
    family: 'communication',
    sub_rubrique: 'automatic_messages',
    criticality: 'blocking_default',
    label_key: 'ONBOARDING_TASK_COMMUNICATION_AUTOMATIC_MESSAGES',
    weight: 0,
    api_endpoint: 'PUT /app/notification-preference',
    menu_target: { route: '/marketing/notifications' },
    required_scopes: ['establishment.info.update'],
    mcp_feature: {
      intent:
        'Confirmation automatique des réservations + message de confirmation et rappel 24 h actifs (étape 2 du tunnel). Un rappel désactivé vaut un avertissement, jamais un blocage.',
    },
  },
  {
    id: 'communication.share_link',
    family: 'communication',
    sub_rubrique: 'invitations',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_COMMUNICATION_SHARE_LINK',
    weight: 0,
    api_endpoint: 'POST /app/onboarding/tunnel/link-shared',
    menu_target: { route: '/agenda' },
    mcp_feature: {
      intent:
        'Le pro a fait vivre son lien de réservation : partage natif / copie, saisie rapide de clientes ou import, ou campagne envoyée (étape 3 du tunnel, ignorable).',
    },
  },
  {
    id: 'company.profile.gmb_push_done',
    family: 'company',
    sub_rubrique: 'profile',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_COMPANY_PROFILE_GMB_PUSH_DONE',
    weight: 0,
    api_endpoint: 'POST /app/pro-place/gmb/push',
    menu_target: { route: '/establishment/gmb' },
    required_scopes: ['establishment.google_sync.update'],
    conditional: 'gmb_connected',
    mcp_feature: {
      intent:
        'Au moins une fiche Google reliée a reçu un push de l’existant (description, horaires, prestations…) — étape 5 du tunnel.',
      prerequisites: [{ ref: 'company.profile.gmb', why: 'Il faut une fiche reliée avant de pousser.' }],
    },
  },
  {
    id: 'payments.conditions.rule',
    family: 'payments',
    sub_rubrique: 'conditions',
    criticality: 'blocking_default',
    label_key: 'ONBOARDING_TASK_PAYMENTS_CONDITIONS_RULE',
    weight: 0,
    api_endpoint: 'POST /app/onboarding/tunnel/payment-rule',
    menu_target: { route: '/establishment', sub_tab: 'payments' },
    required_scopes: ['establishment.stripe_connect.update'],
    mcp_feature: {
      intent:
        'Une règle de paiement pour toutes les prestations (acompte / empreinte / aucun paiement), enregistrée AVANT Stripe (`pending_payment_rule`) et appliquée dès que Stripe encaisse — étape 6 du tunnel. « Aucun paiement » est une règle valide.',
    },
  },

  // ─── Famille 🔌 Mes intégrations — tâches différées ────────────────────────
  {
    id: 'integrations.booking_widget',
    family: 'integrations',
    sub_rubrique: 'widgets',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_INTEGRATIONS_BOOKING_WIDGET',
    weight: 1,
    api_endpoint: 'GET /app/integrations/booking-widget',
    menu_target: { route: '/integrations', sub_tab: 'widgets' },
    required_scopes: ['website.builder.read'],
  },
  {
    id: 'integrations.gift_card_widget',
    family: 'integrations',
    sub_rubrique: 'widgets',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_INTEGRATIONS_GIFT_CARD_WIDGET',
    weight: 0,
    api_endpoint: 'GET /app/integrations/gift-card-widget',
    menu_target: { route: '/integrations', sub_tab: 'widgets' },
    required_scopes: ['website.builder.read'],
    conditional: 'stripe_connected',
  },
  {
    id: 'integrations.reviews_widget',
    family: 'integrations',
    sub_rubrique: 'widgets',
    criticality: 'deferred',
    label_key: 'ONBOARDING_TASK_INTEGRATIONS_REVIEWS_WIDGET',
    weight: 0,
    api_endpoint: 'GET /app/integrations/reviews-widget',
    menu_target: { route: '/integrations', sub_tab: 'widgets' },
    required_scopes: ['website.builder.read'],
    conditional: 'gmb_connected',
  },
];

const FAMILIES: OnboardingFamily[] = [
  {
    id: 'company',
    order: 1,
    icon: '🏢',
    name_key: 'ONBOARDING_FAMILY_COMPANY',
    menu: { route: '/establishment' },
    sub_rubriques: [
      { id: 'profile', label_key: 'ONBOARDING_SUBRUBRIQUE_COMPANY_PROFILE' },
      { id: 'media', label_key: 'ONBOARDING_SUBRUBRIQUE_COMPANY_MEDIA' },
      { id: 'team', label_key: 'ONBOARDING_SUBRUBRIQUE_COMPANY_TEAM' },
      { id: 'places', label_key: 'ONBOARDING_SUBRUBRIQUE_COMPANY_PLACES' },
      { id: 'equipment', label_key: 'ONBOARDING_SUBRUBRIQUE_COMPANY_EQUIPMENT' },
      { id: 'settings', label_key: 'ONBOARDING_SUBRUBRIQUE_COMPANY_SETTINGS' },
    ],
  },
  {
    id: 'catalog',
    order: 2,
    icon: '💇',
    name_key: 'ONBOARDING_FAMILY_CATALOG',
    menu: { route: '/catalog' },
    sub_rubriques: [
      { id: 'prestations', label_key: 'ONBOARDING_SUBRUBRIQUE_CATALOG_PRESTATIONS' },
      { id: 'packs', label_key: 'ONBOARDING_SUBRUBRIQUE_CATALOG_PACKS' },
      { id: 'gift_cards', label_key: 'ONBOARDING_SUBRUBRIQUE_CATALOG_GIFT_CARDS' },
    ],
  },
  {
    id: 'agenda',
    order: 3,
    icon: '📅',
    name_key: 'ONBOARDING_FAMILY_AGENDA',
    menu: { route: '/agenda' },
    sub_rubriques: [
      { id: 'hours', label_key: 'ONBOARDING_SUBRUBRIQUE_AGENDA_HOURS' },
      { id: 'settings', label_key: 'ONBOARDING_SUBRUBRIQUE_AGENDA_SETTINGS' },
      { id: 'google_sync', label_key: 'ONBOARDING_SUBRUBRIQUE_AGENDA_GOOGLE_SYNC' },
      { id: 'workshops', label_key: 'ONBOARDING_SUBRUBRIQUE_AGENDA_WORKSHOPS' },
    ],
  },
  {
    id: 'payments',
    order: 4,
    icon: '💳',
    name_key: 'ONBOARDING_FAMILY_PAYMENTS',
    menu: { route: '/establishment', sub_tab: 'payments' },
    sub_rubriques: [
      { id: 'stripe_account', label_key: 'ONBOARDING_SUBRUBRIQUE_PAYMENTS_STRIPE_ACCOUNT' },
      { id: 'conditions', label_key: 'ONBOARDING_SUBRUBRIQUE_PAYMENTS_CONDITIONS' },
    ],
  },
  {
    id: 'website',
    order: 5,
    icon: '🌐',
    name_key: 'ONBOARDING_FAMILY_WEBSITE',
    menu: { route: '/website' },
    sub_rubriques: [
      { id: 'customize', label_key: 'ONBOARDING_SUBRUBRIQUE_WEBSITE_CUSTOMIZE' },
      { id: 'publish', label_key: 'ONBOARDING_SUBRUBRIQUE_WEBSITE_PUBLISH' },
      { id: 'domain', label_key: 'ONBOARDING_SUBRUBRIQUE_WEBSITE_DOMAIN' },
    ],
  },
  {
    id: 'customers',
    order: 6,
    icon: '👥',
    name_key: 'ONBOARDING_FAMILY_CUSTOMERS',
    menu: { route: '/customers' },
    sub_rubriques: [
      { id: 'base', label_key: 'ONBOARDING_SUBRUBRIQUE_CUSTOMERS_BASE' },
      { id: 'tags', label_key: 'ONBOARDING_SUBRUBRIQUE_CUSTOMERS_TAGS' },
    ],
  },
  {
    id: 'communication',
    order: 7,
    icon: '📣',
    name_key: 'ONBOARDING_FAMILY_COMMUNICATION',
    menu: { route: '/marketing/campaigns' },
    sub_rubriques: [
      { id: 'invitations', label_key: 'ONBOARDING_SUBRUBRIQUE_COMMUNICATION_INVITATIONS' },
      { id: 'automatic_messages', label_key: 'ONBOARDING_SUBRUBRIQUE_COMMUNICATION_AUTOMATIC_MESSAGES' },
    ],
  },
  {
    id: 'integrations',
    order: 8,
    icon: '🔌',
    name_key: 'ONBOARDING_FAMILY_INTEGRATIONS',
    menu: { route: '/integrations' },
    sub_rubriques: [{ id: 'widgets', label_key: 'ONBOARDING_SUBRUBRIQUE_INTEGRATIONS_WIDGETS' }],
    conditional: 'has_existing_website',
  },
];

const ENTRY: OnboardingScreen = {
  id: 'entry',
  title_key: 'ONBOARDING_SCREEN_ENTRY_TITLE',
  subtitle_key: 'ONBOARDING_SCREEN_ENTRY_SUBTITLE',
  task_ids: ['entry.intention'],
};

const STEPS: OnboardingStep[] = [
  {
    id: 'identity',
    order: 1,
    title_key: 'ONBOARDING_STEP_IDENTITY',
    screens: [
      {
        id: 'identity_core',
        title_key: 'ONBOARDING_SCREEN_IDENTITY_CORE_TITLE',
        subtitle_key: 'ONBOARDING_SCREEN_IDENTITY_CORE_SUBTITLE',
        task_ids: [
          'company.profile.business_name',
          'company.profile.activities',
          'company.profile.city',
          'company.profile.phone',
          'company.media.logo',
        ],
      },
      {
        id: 'online_presence',
        title_key: 'ONBOARDING_SCREEN_ONLINE_PRESENCE_TITLE',
        subtitle_key: 'ONBOARDING_SCREEN_ONLINE_PRESENCE_SUBTITLE',
        task_ids: [
          'company.profile.website_url',
          'company.profile.gmb',
          'company.profile.instagram',
          'company.profile.other_link',
        ],
      },
    ],
  },
  {
    id: 'team',
    order: 2,
    title_key: 'ONBOARDING_STEP_TEAM',
    screens: [
      {
        id: 'team_size',
        title_key: 'ONBOARDING_SCREEN_TEAM_SIZE_TITLE',
        subtitle_key: 'ONBOARDING_SCREEN_TEAM_SIZE_SUBTITLE',
        task_ids: ['company.team.structure'],
      },
    ],
  },
  {
    id: 'prestations',
    order: 3,
    title_key: 'ONBOARDING_STEP_PRESTATIONS',
    screens: [
      {
        id: 'prestations',
        title_key: 'ONBOARDING_SCREEN_PRESTATIONS_TITLE',
        subtitle_key: 'ONBOARDING_SCREEN_PRESTATIONS_SUBTITLE',
        task_ids: ['catalog.prestations.create'],
      },
    ],
  },
  {
    id: 'booking',
    order: 4,
    title_key: 'ONBOARDING_STEP_BOOKING',
    screens: [
      {
        id: 'locations',
        title_key: 'ONBOARDING_SCREEN_LOCATIONS_TITLE',
        subtitle_key: 'ONBOARDING_SCREEN_LOCATIONS_SUBTITLE',
        task_ids: ['company.places.location_types'],
      },
      {
        id: 'opening_hours',
        title_key: 'ONBOARDING_SCREEN_OPENING_HOURS_TITLE',
        subtitle_key: 'ONBOARDING_SCREEN_OPENING_HOURS_SUBTITLE',
        task_ids: ['agenda.hours.opening_hours'],
      },
    ],
  },
];

/**
 * TUNNEL OBLIGATOIRE (parcours `OnboardingFlow.V2`) — spec « Onboarding Propelsia » v4,
 * section C. Six étapes, dans cet ordre, avant tout accès à l'espace pro. Chaque étape écrit
 * dans les vraies tables ; « fait » est toujours lu par les évaluateurs du score, jamais
 * déclaré. Les étapes ignorables posent un report (`deferred_items`) qui remonte après coup.
 */
const TUNNEL: OnboardingTunnelStep[] = [
  {
    id: 'offer',
    order: 1,
    title_key: 'ONBOARDING_TUNNEL_STEP_OFFER',
    route: '/onboarding/tunnel/offer',
    // Sans prestation (prix + durée), pas de lien : non ignorable. L'à-propos, lui, se reporte.
    required_task_ids: ['catalog.prestations.create'],
    optional_task_ids: [],
    deferred_kinds: ['about'],
    skippable: false,
  },
  {
    id: 'places_hours',
    order: 2,
    title_key: 'ONBOARDING_TUNNEL_STEP_PLACES_HOURS',
    route: '/onboarding/tunnel/places_hours',
    required_task_ids: [
      'company.places.location_types',
      'agenda.hours.opening_hours',
      'communication.automatic_messages',
    ],
    optional_task_ids: [],
    deferred_kinds: [],
    skippable: false,
  },
  {
    id: 'share',
    order: 3,
    title_key: 'ONBOARDING_TUNNEL_STEP_SHARE',
    route: '/onboarding/tunnel/share',
    // Victoire « votre lien est vivant » : un partage OU un import suffit, sinon « ignorer ».
    required_task_ids: ['communication.share_link'],
    optional_task_ids: ['customers.base.import'],
    deferred_kinds: ['share_link'],
    skippable: true,
    skip_kind: 'share_link',
  },
  {
    id: 'website',
    order: 4,
    title_key: 'ONBOARDING_TUNNEL_STEP_WEBSITE',
    route: '/onboarding/tunnel/website',
    // « Ignorer » = publier avec le style du secteur : l'étape se termine donc toujours par une
    // publication (report `website_style` ouvert pour « personnaliser » dans Et maintenant).
    required_task_ids: ['website.publish'],
    optional_task_ids: ['company.media.logo', 'company.media.photos'],
    deferred_kinds: ['website_style', 'logo_photos'],
    skippable: false,
  },
  {
    id: 'seo',
    order: 5,
    title_key: 'ONBOARDING_TUNNEL_STEP_SEO',
    route: '/onboarding/tunnel/seo',
    // Les villes ne se reportent pas ; la fiche Google oui (« pas encore de fiche »).
    required_task_ids: ['company.profile.service_areas'],
    optional_task_ids: ['company.profile.gmb', 'company.profile.gmb_push_done'],
    deferred_kinds: ['fiche_google'],
    skippable: false,
  },
  {
    id: 'payment',
    order: 6,
    title_key: 'ONBOARDING_TUNNEL_STEP_PAYMENT',
    route: '/onboarding/tunnel/payment',
    // La règle (même « aucun paiement ») est obligatoire ; Stripe se reporte.
    required_task_ids: ['payments.conditions.rule'],
    optional_task_ids: ['payments.stripe.create_account'],
    deferred_kinds: ['stripe'],
    skippable: false,
  },
];

export const ONBOARDING_REGISTRY: OnboardingRegistry = {
  entry: ENTRY,
  steps: STEPS,
  families: FAMILIES,
  tasks: TASKS,
  tunnel: TUNNEL,
};

/** Index `id → OnboardingTunnelStep` pour lookup O(1). */
export const ONBOARDING_TUNNEL_STEP_BY_ID: ReadonlyMap<string, OnboardingTunnelStep> = new Map(
  TUNNEL.map((step): [string, OnboardingTunnelStep] => [step.id, step]),
);

/**
 * Étapes à compter pour le bonus « tout prêt en 7 jours » (spec v4 section I) : 5 étapes sur 6,
 * tout sauf Stripe — or Stripe n'est qu'une tâche optionnelle de l'étape 6, dont la règle de
 * paiement est obligatoire. Le bonus se calcule donc sur les 6 étapes « faites ou ignorées ».
 */
export const ONBOARDING_TUNNEL_BONUS_STEP_COUNT = TUNNEL.length;

/** Index `id → OnboardingTask` pour lookup O(1). */
export const ONBOARDING_TASK_BY_ID: ReadonlyMap<string, OnboardingTask> = new Map(
  TASKS.map((task): [string, OnboardingTask] => [task.id, task]),
);

/** Index `id → OnboardingFamily` pour lookup O(1). */
export const ONBOARDING_FAMILY_BY_ID: ReadonlyMap<string, OnboardingFamily> = new Map(
  FAMILIES.map((family): [string, OnboardingFamily] => [family.id, family]),
);

/**
 * Assistant d'onboarding TERMINÉ : plus d'étape courante et toutes les étapes du registre
 * complétées. Source unique de cette règle (redirection du wizard, bannière de reprise, lien de
 * reprise d'essai, activation d'une entreprise supplémentaire après paiement).
 */
export const isOnboardingWizardDone = (
  state: { current_step?: string | null; completed_steps?: unknown } | null | undefined,
): boolean =>
  !!state &&
  state.current_step === null &&
  Array.isArray(state.completed_steps) &&
  state.completed_steps.length >= ONBOARDING_REGISTRY.steps.length;
